import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { adminApi, type CreateTeamDto } from '../../api/adminApi';
import { resultsApi } from '../../api/resultsApi';
import type { Category, Team } from '../../types/domain';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface Props {
  competitionId: number;
  categories: Category[];
}

export const CompetitionTeamsTab = ({ competitionId, categories }: Props) => {
  const [categoryIdx, setCategoryIdx] = useState(0);
  const [editing, setEditing] = useState<Team | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const currentCategory = categories[categoryIdx];

  const { data: teams = [] } = useQuery({
    queryKey: ['admin', 'category', currentCategory?.id, 'teams'],
    queryFn: () => (currentCategory ? adminApi.listTeams(currentCategory.id) : Promise.resolve([])),
    enabled: !!currentCategory,
  });

  const { data: allResults = [] } = useQuery({
    queryKey: ['admin', 'results', competitionId],
    queryFn: () => resultsApi.listByCompetition(competitionId),
  });

  const countResultsForTeam = (teamId: number) =>
    allResults.filter((r) => r.teamId === teamId).length;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'category', currentCategory?.id, 'teams'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'competition', competitionId, 'categories'] });
  };

  const createMutation = useMutation({
    mutationFn: (dto: CreateTeamDto) => adminApi.createTeam(currentCategory!.id, dto),
    onSuccess: () => {
      enqueueSnackbar('Družstvo přidáno', { variant: 'success' });
      invalidate();
      setCreating(false);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CreateTeamDto }) => adminApi.updateTeam(id, dto),
    onSuccess: () => {
      enqueueSnackbar('Družstvo uloženo', { variant: 'success' });
      invalidate();
      setEditing(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminApi.deleteTeam(id),
    onSuccess: () => {
      enqueueSnackbar('Družstvo smazáno', { variant: 'success' });
      invalidate();
      setDeletingTeam(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const reorderMutation = useMutation({
    mutationFn: ({ id, toStartNumber }: { id: number; toStartNumber: number }) =>
      adminApi.reorderTeam(id, toStartNumber),
    onSuccess: () => invalidate(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při přesunu', { variant: 'error' }),
  });

  const renumberMutation = useMutation({
    mutationFn: (categoryId: number) => adminApi.renumberTeams(categoryId),
    onSuccess: () => {
      enqueueSnackbar('Startovní čísla přepočítána', { variant: 'success' });
      invalidate();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při přepočtu', { variant: 'error' }),
  });

  if (categories.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">Vytvořte nejdřív kategorie.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Tabs
        value={categoryIdx}
        onChange={(_, v) => setCategoryIdx(v)}
        variant={categories.length > 4 ? 'scrollable' : 'fullWidth'}
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.05em',
            fontSize: '0.9375rem',
            minHeight: 56,
            flex: categories.length > 4 ? undefined : 1,
          },
        }}
      >
        {categories.map((c) => (
          <Tab key={c.id} label={`${c.name} (${c.teamCount})`} />
        ))}
      </Tabs>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{currentCategory?.name} — startovní listina</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<FormatListNumberedIcon />}
                variant="outlined"
                onClick={() => currentCategory && renumberMutation.mutate(currentCategory.id)}
                disabled={!currentCategory || renumberMutation.isPending || teams.length === 0}
              >
                Přepočítat pořadí
              </Button>
              <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreating(true)}>
                Přidat družstvo
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={1}>
            {teams.map((t, idx) => (
              <Card key={t.id} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ minWidth: 60 }}>
                      <Typography fontWeight={700} fontSize="1.5rem" color="primary">
                        {t.startNumber}.
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={600}>{t.name}</Typography>
                      {t.note && (
                        <Typography variant="caption" color="text.secondary">
                          {t.note}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="column" spacing={0}>
                      <Tooltip title="Posunout nahoru">
                        <span>
                          <IconButton
                            size="small"
                            disabled={idx === 0 || reorderMutation.isPending}
                            onClick={() =>
                              reorderMutation.mutate({
                                id: t.id,
                                toStartNumber: t.startNumber - 1,
                              })
                            }
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Posunout dolů">
                        <span>
                          <IconButton
                            size="small"
                            disabled={idx === teams.length - 1 || reorderMutation.isPending}
                            onClick={() =>
                              reorderMutation.mutate({
                                id: t.id,
                                toStartNumber: t.startNumber + 1,
                              })
                            }
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                    <Tooltip title="Upravit">
                      <IconButton onClick={() => setEditing(t)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Smazat">
                      <IconButton color="error" onClick={() => setDeletingTeam(t)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {teams.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Žádná družstva. Klikni „Přidat družstvo".
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <TeamFormDialog
        open={creating || !!editing}
        team={editing}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSubmit={(dto) => {
          if (editing) {
            updateMutation.mutate({ id: editing.id, dto });
          } else {
            createMutation.mutate(dto);
          }
        }}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      <ConfirmDialog
        open={!!deletingTeam}
        title="Smazat družstvo?"
        message={
          deletingTeam
            ? (() => {
                const resCount = countResultsForTeam(deletingTeam.id);
                const base = `Družstvo "${deletingTeam.name}" (#${deletingTeam.startNumber}) bude trvale smazáno.`;
                if (resCount > 0) {
                  return `⚠️ ${base} Ztratíte ${resCount} zapsaných výsledků. Tato akce je nevratná.`;
                }
                return base;
              })()
            : ''
        }
        destructive
        loading={deleteMutation.isPending}
        onCancel={() => setDeletingTeam(null)}
        onConfirm={() => deletingTeam && deleteMutation.mutate(deletingTeam.id)}
      />
    </Stack>
  );
};

interface TeamFormDialogProps {
  open: boolean;
  team: Team | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (dto: CreateTeamDto) => void;
}

const TeamFormDialog = ({ open, team, loading, onCancel, onSubmit }: TeamFormDialogProps) => {
  const [name, setName] = useState('');
  const [startNumber, setStartNumber] = useState<number | ''>('');
  const [note, setNote] = useState('');

  const handleEnter = () => {
    if (team) {
      setName(team.name);
      setStartNumber(team.startNumber);
      setNote(team.note ?? '');
    } else {
      setName('');
      setStartNumber('');
      setNote('');
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle>{team ? 'Upravit družstvo' : 'Nové družstvo'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Název družstva" value={name} onChange={(e) => setName(e.target.value)} required fullWidth autoFocus />
          {!team && (
            <TextField
              label="Startovní číslo (volitelné)"
              type="number"
              value={startNumber}
              onChange={(e) => setStartNumber(e.target.value ? Number(e.target.value) : '')}
              helperText="Pokud nevyplníš, zařadí se na konec. Pokud je číslo obsazené, ostatní družstva se posunou."
              inputProps={{ min: 1 }}
            />
          )}
          {team && (
            <Typography variant="caption" color="text.secondary">
              Pořadí se mění šipkami v seznamu (startovní číslo {team.startNumber}).
            </Typography>
          )}
          <TextField label="Poznámka" value={note} onChange={(e) => setNote(e.target.value)} multiline rows={2} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onSubmit({
              name,
              startNumber: team ? undefined : startNumber === '' ? undefined : startNumber,
              note: note || undefined,
            })
          }
          disabled={loading || !name}
        >
          {team ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
